import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Store,
  Tag,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  Edit,
  Trash2,
  Upload,
  X,
  Image
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog states
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Form states
  const [businessForm, setBusinessForm] = useState({
    name: '',
    name_ta: '',
    category_id: '',
    description: '',
    description_ta: '',
    address: '',
    address_ta: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    latitude: 10.9601,
    longitude: 78.0766,
    images: [],
    working_hours: {},
    status: 'approved'
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name_en: '',
    name_ta: '',
    description_en: '',
    description_ta: '',
    icon: 'Store',
    image_url: ''
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Check if user is admin
    if (!user) {
      // Not logged in - redirect to login
      navigate('/login', { state: { from: { pathname: '/admin' } } });
      return;
    }
    
    if (user.role !== 'admin') {
      // Logged in but not admin - redirect to home
      navigate('/');
      return;
    }
    
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, pendingRes, usersRes, categoriesRes, allBizRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/businesses/pending`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/businesses/all`)
      ]);
      setStats(statsRes.data);
      setPendingBusinesses(pendingRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
      setAllBusinesses(allBizRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (businessId) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/approve`);
      toast.success(language === 'ta' ? 'கடை அங்கீகரிக்கப்பட்டது' : 'Business approved');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleReject = async (businessId) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/reject`);
      toast.success(language === 'ta' ? 'கடை நிராகரிக்கப்பட்டது' : 'Business rejected');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Image upload handler
  const handleImageUpload = async (e, formType = 'business') => {
    const files = e.target.files;
    if (!files.length) return;

    setUploadingImage(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const imageUrl = response.data.storage === 's3' 
          ? response.data.url 
          : `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        
        if (formType === 'business') {
          setBusinessForm(prev => ({
            ...prev,
            images: [...prev.images, imageUrl]
          }));
        } else {
          setCategoryForm(prev => ({
            ...prev,
            image_url: imageUrl
          }));
        }
      }
      toast.success(language === 'ta' ? 'படம் பதிவேற்றப்பட்டது' : 'Image uploaded');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeBusinessImage = (index) => {
    setBusinessForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Business CRUD
  const openAddBusinessDialog = () => {
    setEditingBusiness(null);
    setBusinessForm({
      name: '',
      name_ta: '',
      category_id: '',
      description: '',
      description_ta: '',
      address: '',
      address_ta: '',
      phone: '',
      whatsapp: '',
      email: '',
      website: '',
      latitude: 10.9601,
      longitude: 78.0766,
      images: [],
      working_hours: {},
      status: 'approved'
    });
    setBusinessDialogOpen(true);
  };

  const openEditBusinessDialog = (business) => {
    setEditingBusiness(business);
    setBusinessForm({
      name: business.name,
      name_ta: business.name_ta || '',
      category_id: business.category_id,
      description: business.description,
      description_ta: business.description_ta || '',
      address: business.address,
      address_ta: business.address_ta || '',
      phone: business.phone,
      whatsapp: business.whatsapp || '',
      email: business.email || '',
      website: business.website || '',
      latitude: business.latitude,
      longitude: business.longitude,
      images: business.images || [],
      working_hours: business.working_hours || {},
      status: business.status
    });
    setBusinessDialogOpen(true);
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    
    if (!businessForm.category_id) {
      toast.error(language === 'ta' ? 'வகையைத் தேர்ந்தெடுக்கவும்' : 'Please select a category');
      return;
    }

    try {
      if (editingBusiness) {
        await axios.put(`${API}/admin/businesses/${editingBusiness.id}`, {
          ...businessForm,
          latitude: parseFloat(businessForm.latitude),
          longitude: parseFloat(businessForm.longitude)
        });
        toast.success(language === 'ta' ? 'கடை புதுப்பிக்கப்பட்டது' : 'Business updated');
      } else {
        await axios.post(`${API}/admin/businesses`, {
          ...businessForm,
          latitude: parseFloat(businessForm.latitude),
          longitude: parseFloat(businessForm.longitude)
        });
        toast.success(language === 'ta' ? 'கடை சேர்க்கப்பட்டது' : 'Business added');
      }
      setBusinessDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}`);
      toast.success(language === 'ta' ? 'கடை நீக்கப்பட்டது' : 'Business deleted');
      fetchData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Category CRUD
  const openAddCategoryDialog = () => {
    setEditingCategory(null);
    setCategoryForm({
      name_en: '',
      name_ta: '',
      description_en: '',
      description_ta: '',
      icon: 'Store',
      image_url: ''
    });
    setCategoryDialogOpen(true);
  };

  const openEditCategoryDialog = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name_en: category.name_en,
      name_ta: category.name_ta,
      description_en: category.description_en,
      description_ta: category.description_ta,
      icon: category.icon,
      image_url: category.image_url
    });
    setCategoryDialogOpen(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryForm);
        toast.success(language === 'ta' ? 'வகை புதுப்பிக்கப்பட்டது' : 'Category updated');
      } else {
        await axios.post(`${API}/categories`, categoryForm);
        toast.success(language === 'ta' ? 'வகை சேர்க்கப்பட்டது' : 'Category added');
      }
      setCategoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      toast.success(language === 'ta' ? 'வகை நீக்கப்பட்டது' : 'Category deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  // User deletion
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success(language === 'ta' ? 'பயனர் நீக்கப்பட்டது' : 'User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">{t('loading')}</div>
      </div>
    );
  }

  // If not admin after loading, show nothing (redirect will happen)
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Redirecting...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-page">
      {/* Header */}
      <div className="bg-slate-900 py-6">
        <div className="container mx-auto px-4 md:px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('home')}
          </Button>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">
            {t('adminPanel')}
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              title={language === 'ta' ? 'மொத்த பயனர்கள்' : 'Total Users'}
              value={stats.total_users}
              icon={Users}
              color="bg-blue-600"
            />
            <StatCard
              title={language === 'ta' ? 'மொத்த கடைகள்' : 'Total Shops'}
              value={stats.total_businesses}
              icon={Store}
              color="bg-green-600"
            />
            <StatCard
              title={language === 'ta' ? 'நிலுவையில் உள்ளவை' : 'Pending'}
              value={stats.pending_businesses}
              icon={Clock}
              color="bg-yellow-500"
            />
            <StatCard
              title={language === 'ta' ? 'வகைகள்' : 'Categories'}
              value={stats.total_categories}
              icon={Tag}
              color="bg-purple-600"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="pending" data-testid="tab-pending">
              {t('pendingApprovals')} ({pendingBusinesses.length})
            </TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">
              {t('allBusinesses')}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              {t('allUsers')}
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              {t('manageCategories')}
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>{t('pendingApprovals')}</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingBusinesses.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {language === 'ta' ? 'நிலுவையில் எதுவும் இல்லை' : 'No pending approvals'}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('shopName')}</TableHead>
                        <TableHead>{t('category')}</TableHead>
                        <TableHead>{t('phone')}</TableHead>
                        <TableHead>{language === 'ta' ? 'செயல்கள்' : 'Actions'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingBusinesses.map((biz) => (
                        <TableRow key={biz.id} data-testid={`pending-row-${biz.id}`}>
                          <TableCell className="font-medium">{biz.name}</TableCell>
                          <TableCell>{language === 'ta' ? biz.category_name_ta : biz.category_name_en}</TableCell>
                          <TableCell>{biz.phone}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/business/${biz.id}`)}
                                data-testid={`view-pending-${biz.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApprove(biz.id)}
                                data-testid={`approve-${biz.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                {t('approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReject(biz.id)}
                                data-testid={`reject-${biz.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                {t('reject')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Businesses */}
          <TabsContent value="businesses">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('allBusinesses')}</CardTitle>
                <Button onClick={openAddBusinessDialog} className="bg-[#E85A24] hover:bg-[#d14918]" data-testid="add-business-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'ta' ? 'கடை சேர்' : 'Add Business'}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'ta' ? 'படம்' : 'Image'}</TableHead>
                      <TableHead>{t('shopName')}</TableHead>
                      <TableHead>{t('category')}</TableHead>
                      <TableHead>{language === 'ta' ? 'நிலை' : 'Status'}</TableHead>
                      <TableHead>{t('rating')}</TableHead>
                      <TableHead>{language === 'ta' ? 'செயல்கள்' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allBusinesses.map((biz) => (
                      <TableRow key={biz.id} data-testid={`biz-row-${biz.id}`}>
                        <TableCell>
                          <img 
                            src={biz.images?.[0] || 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=100'} 
                            alt={biz.name} 
                            className="w-12 h-12 rounded object-cover" 
                          />
                        </TableCell>
                        <TableCell className="font-medium">{biz.name}</TableCell>
                        <TableCell>{language === 'ta' ? biz.category_name_ta : biz.category_name_en}</TableCell>
                        <TableCell>
                          <Badge variant={biz.status === 'approved' ? 'default' : biz.status === 'pending' ? 'outline' : 'destructive'}>
                            {biz.status}
                          </Badge>
                        </TableCell>
                        <TableCell>⭐ {biz.avg_rating} ({biz.review_count})</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditBusinessDialog(biz)} data-testid={`edit-biz-${biz.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" data-testid={`delete-biz-${biz.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{language === 'ta' ? 'நீக்க உறுதிப்படுத்தவும்' : 'Confirm Delete'}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {language === 'ta' ? 'இந்த கடையை நிரந்தரமாக நீக்க விரும்புகிறீர்களா?' : 'Are you sure you want to delete this business?'}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteBusiness(biz.id)} className="bg-red-600">
                                    {t('delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t('allUsers')}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead>{t('email')}</TableHead>
                      <TableHead>{t('phone')}</TableHead>
                      <TableHead>{language === 'ta' ? 'பாத்திரம்' : 'Role'}</TableHead>
                      <TableHead>{language === 'ta' ? 'செயல்கள்' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.role !== 'admin' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" data-testid={`delete-user-${user.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{language === 'ta' ? 'நீக்க உறுதிப்படுத்தவும்' : 'Confirm Delete'}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {language === 'ta' ? 'இந்த பயனரை நீக்க விரும்புகிறீர்களா?' : 'Delete this user and all their data?'}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteUser(user.id)} className="bg-red-600">
                                    {t('delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories */}
          <TabsContent value="categories">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{t('manageCategories')}</CardTitle>
                <Button onClick={openAddCategoryDialog} className="bg-[#E85A24] hover:bg-[#d14918]" data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {language === 'ta' ? 'வகை சேர்' : 'Add Category'}
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'ta' ? 'படம்' : 'Image'}</TableHead>
                      <TableHead>{language === 'ta' ? 'ஆங்கிலம்' : 'English'}</TableHead>
                      <TableHead>{language === 'ta' ? 'தமிழ்' : 'Tamil'}</TableHead>
                      <TableHead>{language === 'ta' ? 'பட்டியல்கள்' : 'Listings'}</TableHead>
                      <TableHead>{language === 'ta' ? 'செயல்கள்' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {categories.map((cat) => (
                      <TableRow key={cat.id} data-testid={`cat-row-${cat.id}`}>
                        <TableCell>
                          <img src={cat.image_url} alt={cat.name_en} className="w-12 h-12 rounded object-cover" />
                        </TableCell>
                        <TableCell className="font-medium">{cat.name_en}</TableCell>
                        <TableCell>{cat.name_ta}</TableCell>
                        <TableCell>{cat.business_count}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditCategoryDialog(cat)} data-testid={`edit-cat-${cat.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="destructive" disabled={cat.business_count > 0} data-testid={`delete-cat-${cat.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>{language === 'ta' ? 'நீக்க உறுதிப்படுத்தவும்' : 'Confirm Delete'}</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {language === 'ta' ? 'இந்த வகையை நீக்க விரும்புகிறீர்களா?' : 'Delete this category?'}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>{language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteCategory(cat.id)} className="bg-red-600">
                                    {t('delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Business Dialog */}
      <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBusiness 
                ? (language === 'ta' ? 'கடை திருத்து' : 'Edit Business')
                : (language === 'ta' ? 'புதிய கடை சேர்' : 'Add New Business')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBusinessSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('shopName')} *</Label>
                <Input
                  value={businessForm.name}
                  onChange={(e) => setBusinessForm({...businessForm, name: e.target.value})}
                  required
                  data-testid="biz-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('shopNameTa')}</Label>
                <Input
                  value={businessForm.name_ta}
                  onChange={(e) => setBusinessForm({...businessForm, name_ta: e.target.value})}
                  data-testid="biz-name-ta-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('category')} *</Label>
                <Select
                  value={businessForm.category_id}
                  onValueChange={(value) => setBusinessForm({...businessForm, category_id: value})}
                >
                  <SelectTrigger data-testid="biz-category-select">
                    <SelectValue placeholder={t('selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {language === 'ta' ? cat.name_ta : cat.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'ta' ? 'நிலை' : 'Status'}</Label>
                <Select
                  value={businessForm.status}
                  onValueChange={(value) => setBusinessForm({...businessForm, status: value})}
                >
                  <SelectTrigger data-testid="biz-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">{t('approved')}</SelectItem>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="rejected">{t('rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('description')} *</Label>
                <Textarea
                  value={businessForm.description}
                  onChange={(e) => setBusinessForm({...businessForm, description: e.target.value})}
                  required
                  rows={3}
                  data-testid="biz-desc-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('descriptionTa')}</Label>
                <Textarea
                  value={businessForm.description_ta}
                  onChange={(e) => setBusinessForm({...businessForm, description_ta: e.target.value})}
                  rows={3}
                  data-testid="biz-desc-ta-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('address')} *</Label>
                <Textarea
                  value={businessForm.address}
                  onChange={(e) => setBusinessForm({...businessForm, address: e.target.value})}
                  required
                  rows={2}
                  data-testid="biz-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('addressTa')}</Label>
                <Textarea
                  value={businessForm.address_ta}
                  onChange={(e) => setBusinessForm({...businessForm, address_ta: e.target.value})}
                  rows={2}
                  data-testid="biz-address-ta-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('phone')} *</Label>
                <Input
                  type="tel"
                  value={businessForm.phone}
                  onChange={(e) => setBusinessForm({...businessForm, phone: e.target.value})}
                  required
                  data-testid="biz-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('whatsapp')}</Label>
                <Input
                  type="tel"
                  value={businessForm.whatsapp}
                  onChange={(e) => setBusinessForm({...businessForm, whatsapp: e.target.value})}
                  data-testid="biz-whatsapp-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('email')}</Label>
                <Input
                  type="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({...businessForm, email: e.target.value})}
                  data-testid="biz-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('website')}</Label>
                <Input
                  type="url"
                  value={businessForm.website}
                  onChange={(e) => setBusinessForm({...businessForm, website: e.target.value})}
                  placeholder="https://"
                  data-testid="biz-website-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('latitude')} *</Label>
                <Input
                  type="number"
                  step="any"
                  value={businessForm.latitude}
                  onChange={(e) => setBusinessForm({...businessForm, latitude: e.target.value})}
                  required
                  data-testid="biz-lat-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('longitude')} *</Label>
                <Input
                  type="number"
                  step="any"
                  value={businessForm.longitude}
                  onChange={(e) => setBusinessForm({...businessForm, longitude: e.target.value})}
                  required
                  data-testid="biz-lng-input"
                />
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label>{t('uploadImages')}</Label>
              <div className="flex flex-wrap gap-4">
                {businessForm.images.map((img, index) => (
                  <div key={index} className="relative w-20 h-20">
                    <img src={img} alt={`Upload ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => removeBusinessImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                  <Upload className="h-5 w-5 text-slate-400" />
                  <span className="text-xs text-slate-400 mt-1">{uploadingImage ? '...' : 'Upload'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload(e, 'business')}
                    className="hidden"
                    disabled={uploadingImage}
                    data-testid="biz-image-upload"
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBusinessDialogOpen(false)}>
                {language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}
              </Button>
              <Button type="submit" className="bg-[#E85A24] hover:bg-[#d14918]" data-testid="save-business-btn">
                {editingBusiness ? (language === 'ta' ? 'புதுப்பி' : 'Update') : (language === 'ta' ? 'சேர்' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingCategory 
                ? (language === 'ta' ? 'வகை திருத்து' : 'Edit Category')
                : (language === 'ta' ? 'புதிய வகை சேர்' : 'Add New Category')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ta' ? 'பெயர் (ஆங்கிலம்)' : 'Name (English)'} *</Label>
                <Input
                  value={categoryForm.name_en}
                  onChange={(e) => setCategoryForm({...categoryForm, name_en: e.target.value})}
                  required
                  data-testid="cat-name-en-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ta' ? 'பெயர் (தமிழ்)' : 'Name (Tamil)'} *</Label>
                <Input
                  value={categoryForm.name_ta}
                  onChange={(e) => setCategoryForm({...categoryForm, name_ta: e.target.value})}
                  required
                  data-testid="cat-name-ta-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ta' ? 'விளக்கம் (ஆங்கிலம்)' : 'Description (English)'}</Label>
                <Input
                  value={categoryForm.description_en}
                  onChange={(e) => setCategoryForm({...categoryForm, description_en: e.target.value})}
                  data-testid="cat-desc-en-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ta' ? 'விளக்கம் (தமிழ்)' : 'Description (Tamil)'}</Label>
                <Input
                  value={categoryForm.description_ta}
                  onChange={(e) => setCategoryForm({...categoryForm, description_ta: e.target.value})}
                  data-testid="cat-desc-ta-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ta' ? 'ஐகான்' : 'Icon'}</Label>
              <Select
                value={categoryForm.icon}
                onValueChange={(value) => setCategoryForm({...categoryForm, icon: value})}
              >
                <SelectTrigger data-testid="cat-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['Store', 'Fish', 'Laptop', 'Hospital', 'Wrench', 'Droplet', 'Sprout', 'Zap', 'ShoppingCart', 'UtensilsCrossed', 'Scissors', 'Sparkles', 'Hammer'].map(icon => (
                    <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Image */}
            <div className="space-y-2">
              <Label>{language === 'ta' ? 'படம்' : 'Image'}</Label>
              <div className="flex items-center gap-4">
                {categoryForm.image_url && (
                  <img src={categoryForm.image_url} alt="Category" className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div>
                  <Input
                    placeholder="Image URL"
                    value={categoryForm.image_url}
                    onChange={(e) => setCategoryForm({...categoryForm, image_url: e.target.value})}
                    className="mb-2"
                    data-testid="cat-image-url-input"
                  />
                  <label className="inline-flex items-center px-4 py-2 border border-slate-300 rounded cursor-pointer hover:bg-slate-50">
                    <Upload className="h-4 w-4 mr-2" />
                    {language === 'ta' ? 'பதிவேற்று' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'category')}
                      className="hidden"
                      disabled={uploadingImage}
                      data-testid="cat-image-upload"
                    />
                  </label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                {language === 'ta' ? 'ரத்துசெய்' : 'Cancel'}
              </Button>
              <Button type="submit" className="bg-[#E85A24] hover:bg-[#d14918]" data-testid="save-category-btn">
                {editingCategory ? (language === 'ta' ? 'புதுப்பி' : 'Update') : (language === 'ta' ? 'சேர்' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
